import { useState, useEffect, useRef, useCallback } from 'react';

const DWELL_MS = 5 * 60 * 1000;
const LEAVE_FACTOR = 1.5;
export const TIME_CHANGED_EVENT = 'lbt:time-changed';

const distanceM = (a, b) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const siteOf = (p) => ({ lat: p.latitude, lng: p.longitude });
const radiusOf = (p) => p.radius_meters || 150;

export const useSiteReminders = (authAxios, enabled) => {
  const [reminder, setReminder] = useState(null);
  const projectsRef = useRef([]);
  const entryRef = useRef(null);
  const posRef = useRef(null);
  const enteredRef = useRef({ siteId: null, since: 0 });
  const dismissedRef = useRef({ clockIn: null, clockOut: null });

  const refreshEntry = useCallback(async () => {
    try {
      const res = await authAxios.get('/time/today');
      entryRef.current = res.data;
    } catch { /* keep last known */ }
    return entryRef.current;
  }, [authAxios]);

  const evaluate = useCallback(async () => {
    const pos = posRef.current;
    if (!pos || !entryRef.current) return;
    const here = projectsRef.current.find((p) => distanceM(pos, siteOf(p)) <= radiusOf(p));
    const isOn = ['working', 'on_break'].includes(entryRef.current.status);

    if (!here) {
      enteredRef.current = { siteId: null, since: 0 };
      dismissedRef.current.clockIn = null;
    } else if (enteredRef.current.siteId !== here.id) {
      enteredRef.current = { siteId: here.id, since: Date.now() };
    }

    if (!isOn && here && dismissedRef.current.clockIn !== here.id && Date.now() - enteredRef.current.since >= DWELL_MS) {
      const fresh = await refreshEntry();
      if (fresh && !['working', 'on_break'].includes(fresh.status)) setReminder({ type: 'clock_in', site: here });
      return;
    }

    const open = isOn && (entryRef.current.sessions || []).find((s) => s.clock_in && !s.clock_out);
    const site = open && projectsRef.current.find((p) => p.id === open.project_id);
    if (!site) return;
    const away = distanceM(pos, siteOf(site)) > radiusOf(site) * LEAVE_FACTOR;
    if (!away) {
      dismissedRef.current.clockOut = null;
    } else if (dismissedRef.current.clockOut !== open.id) {
      const fresh = await refreshEntry();
      if (fresh && ['working', 'on_break'].includes(fresh.status)) setReminder({ type: 'clock_out', site, sessionId: open.id });
    }
  }, [refreshEntry]);

  useEffect(() => {
    if (!enabled || !navigator.geolocation) return undefined;
    authAxios.get('/projects?active_only=true')
      .then((r) => { projectsRef.current = r.data.filter((p) => p.latitude != null && p.longitude != null); })
      .catch(() => {});
    refreshEntry();
    const watchId = navigator.geolocation.watchPosition(
      (p) => { posRef.current = { lat: p.coords.latitude, lng: p.coords.longitude }; evaluate(); },
      () => {},
      { enableHighAccuracy: true, maximumAge: 30000 }
    );
    const poll = setInterval(async () => { await refreshEntry(); evaluate(); }, 60000);
    const onChange = () => { setReminder(null); refreshEntry(); };
    window.addEventListener(TIME_CHANGED_EVENT, onChange);
    return () => {
      navigator.geolocation.clearWatch(watchId);
      clearInterval(poll);
      window.removeEventListener(TIME_CHANGED_EVENT, onChange);
    };
  }, [enabled, authAxios, refreshEntry, evaluate]);

  const dismiss = () => {
    if (reminder?.type === 'clock_in') dismissedRef.current.clockIn = reminder.site.id;
    if (reminder?.type === 'clock_out') dismissedRef.current.clockOut = reminder.sessionId;
    setReminder(null);
  };

  return { reminder, dismiss, position: posRef };
};
